/* eslint-disable eqeqeq */
import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { DSSetup, Service } from 'webapp-types';
import { GoalTargetValueType, TimeUnit } from 'webapp/enums/enums';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputDecimalComponent from 'webapp/components/form/input-decimal';
import FormInputDurationComponent from 'webapp/components/form/input-duration';
import GoalsTargetTypeAndGranulationPopupComponent, {
	BY_END_DATE_TOOLTIP_TEXT,
} from 'webapp/components/goals/target-type-and-granulation/popup';
import ToolTipComponent from 'webapp/components/tool-tip';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type Goal from 'webapp/models-custom/goal';
import { MAX_TARGET_VALUE_LENGTH } from 'webapp/models-custom/goal';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type {
	GoalRangeTimeUnit,
	TargetTypeAndGranulationOnChange,
} from 'webapp/services/model-manager/goals';

interface Args {
	model: Goal;
	targetOtherMetric?: DSSetup.MetricSetupData;
	isDisabled?: boolean;
	hideOtherMetric?: boolean;
	changeTargetValue: (value: number | null) => void;
	changeTargetRange: TargetTypeAndGranulationOnChange;
}
export default class GoalsTargetTypeAndGranulationDropdownComponent extends Component<Args> {
	@service('infolayer') declare infolayer: Service.Infolayer;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('timeframe') declare timeframeService: Service.Timeframe;

	@tracked _value: string | null;
	@tracked isPopupOpen = false;
	@tracked updateInputValue = false;

	maxTargetValueLength = MAX_TARGET_VALUE_LENGTH;
	byEndDateTooltipText = BY_END_DATE_TOOLTIP_TEXT;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this._value = this.model.targetValue != null ? String(this.model.targetValue) : null;
	}

	get model() {
		return this.args.model;
	}

	get isNewGoal() {
		return this.model.isNew;
	}

	get isDisabled() {
		return this.args.isDisabled || this.model.historyValuesOverride;
	}

	get isPrimaryGranulationTotal() {
		return this.model.primaryGranulation === TimeUnit.TOTAL;
	}

	get showPercentSign() {
		return this.model.isHistoryValuesPercentFormat && !this.model.isTargetValuePercentFormat;
	}

	get targetValue() {
		return this.model.targetValue;
	}

	get value() {
		return this._value ? String(this._value) : null;
	}

	get showTooltip() {
		return this.isPrimaryGranulationTotal && !this.args.targetOtherMetric?.key;
	}

	get otherMetric() {
		const metricSetupData = this.args.targetOtherMetric;
		if (metricSetupData) {
			const metricSetup = new MetricSetup(metricSetupData);
			return metricSetup.metric;
		}
		return null;
	}

	get dropdownText() {
		if (this.args.targetOtherMetric?.key) {
			return 'Other metric';
		}

		if (this.isPrimaryGranulationTotal) {
			if (this.model.endDate) {
				return `By end of ${moment(this.model.endDate).format('MMM D, YYYY')}`;
			}

			if (this.model.timeframe) {
				return `By end of ${this.timeframeService.getTimeframeString(this.model.timeframe, true)}`;
			}

			return 'By end date';
		}

		return this.mmGoalsService.getGranulationItemString(
			this.model.targetValueType === GoalTargetValueType.FIXED,
			this.model.primaryGranulation,
			true,
		);
	}

	get isFakeInput() {
		return !!this.args.targetOtherMetric || this.model.historyValuesOverride;
	}

	@action
	updateOnOutsideValueChange() {
		this.updateInputValue = true;

		this._value = this.targetValue !== null ? String(this.targetValue) : null;

		next(() => {
			this.updateInputValue = false;
		});
	}

	@action
	updateValue(value: number | null) {
		if (String(value) !== String(this.targetValue)) {
			this._value = String(value);
			this.args.changeTargetValue(value);
		}
	}

	@action
	openIntervalDropdown(event: PointerEvent): void {
		if (this.isPopupOpen) {
			return;
		}

		const dropDownEl = (event.target as HTMLElement).closest('.dropdown')!,
			attachTo = dropDownEl.querySelector<HTMLElement>('ui-icon')!;

		if (!attachTo) {
			return;
		}

		const popup = this.popupService.createContext(
			GoalsTargetTypeAndGranulationPopupComponent,
			attachTo,
			{
				popupClass: 'optionspopup popup-type-goal-target-type-and-granulation',
			},
		);

		popup.open({
			isAutomaticGoal: this.model.isAutomaticGoal,
			targetValueType: this.model.targetValueType,
			primaryGranulation: this.model.primaryGranulation,
			targetOtherMetric: this.args.targetOtherMetric,
			allowByEndDate: this.model.useEndDateForCalculations,
			hideOtherMetric: this.args.hideOtherMetric,
			onChange: this.onChange,
		});
	}

	@action
	onChange(
		targetValueType: GoalTargetValueType,
		primaryGranulation: GoalRangeTimeUnit,
		otherMetric?: DSSetup.MetricSetupData,
	) {
		if (otherMetric?.key === this.model.goalKeyLong && !this.model.isNew) {
			this.infolayer.error('Goal can’t be set as the target of itself');
			return;
		}

		this.args.changeTargetRange(targetValueType, primaryGranulation, otherMetric);
	}
	<template>
		<div
			class={{joinClasses
				(array
					"input-dropdown" (if this.isFakeInput "metric") (if this.isDisabled "disabled")
				)
			}}
			{{didUpdate this.updateOnOutsideValueChange @model.targetValue}}
		>
			{{#if this.isFakeInput}}
				{{#if this.model.historyValuesOverride}}
					<span>
						Custom
						<ToolTipComponent
							@text="Custom targets are set in advanced period settings."
							@position="top"
							@maxWidth={{250}}
						/>
					</span>
				{{else}}
					<img
						src={{this.otherMetric.sourceAvatar}}
						alt={{this.otherMetric.sourceName}}
					/><span>{{this.otherMetric.name}}</span>
				{{/if}}
			{{else}}
				{{#if this.model.useDurationInput}}
					<FormInputDurationComponent
						@value={{this.value}}
						@onChange={{this.updateValue}}
						@isDisabled={{this.isDisabled}}
						@forceUpdateFromValue={{this.updateInputValue}}
					/>
				{{else}}
					<FormInputDecimalComponent
						@value={{this.value}}
						@onChange={{this.updateValue}}
						@placeholder="Target value"
						@isDisabled={{this.isDisabled}}
						@forceUpdateFromValue={{this.updateInputValue}}
						@maxLength={{this.maxTargetValueLength}}
					/>
				{{/if}}
			{{/if}}

			<div class="dropdown{{if this.showTooltip ' tooltip'}}">
				<a role="button" {{on "click" this.openIntervalDropdown}}>
					<div>
						{{#if this.showPercentSign}}% {{/if}}
						{{this.dropdownText}}
					</div>
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />
					{{#if this.showTooltip}}
						<ToolTipWithIconComponent
							@text={{this.byEndDateTooltipText}}
							@maxWidth={{230}}
							@iconSize="sm"
						/>
					{{/if}}
				</a>
			</div>
		</div>
	</template>
}
